import { For, Match, Show, Switch } from 'solid-js'
import { bounds, ccw, type Pt, recenter, regular } from '../acoustics/polygon'
import { useApp } from '../controller'
import { MATERIAL_IDS, MATERIALS, type MaterialId } from '../model/materials'
import {
  ENCLOSURE_LIMITS,
  type Enclosure,
  FAMILY_INFO,
  type Face,
  MAX_HOLES,
  MAX_OUTLINE_POINTS,
  MAX_SOURCES,
  type Primitive,
  ROOM_FACES,
  type RoomShape,
  type Source,
  type Vec3,
} from '../model/schema'
import { Btn, NumberInput, type Option, Row, Section, Segmented, SelectRow, SliderRow, Vec3Input } from './controls'
import { OutlineEditor } from './OutlineEditor'
import { clampInside, deg, formatHz, rad, round } from './util'

const MATERIAL_OPTIONS: Option<MaterialId>[] = MATERIAL_IDS.map((id) => ({ value: id, label: MATERIALS[id].label }))
const FACE_LABELS: Record<Face, string> = {
  floor: 'Floor',
  ceiling: 'Ceiling',
  west: 'West wall',
  east: 'East wall',
  north: 'North wall',
  south: 'South wall',
  wall: 'Outer wall',
  core: 'Core column',
}
const CAP_OPTIONS: Option<Primitive['caps']>[] = [
  { value: 'none', label: 'Open both ends' },
  { value: 'top', label: 'Closed top' },
  { value: 'bottom', label: 'Closed bottom' },
  { value: 'both', label: 'Closed both ends' },
]

/** A prism's outline is stored in unit coordinates; the editor works in metres at the object's current size. */
const prismToMetres = (c: Pt[], size: Vec3): Pt[] => c.map(([x, z]) => [x * Math.abs(size[0]), z * Math.abs(size[2])] as Pt)
const prismFromMetres = (c: Pt[], size: Vec3): Pt[] =>
  c.map(([x, z]) => [x / Math.max(Math.abs(size[0]), 0.01), z / Math.max(Math.abs(size[2]), 0.01)] as Pt)

export function Inspector() {
  const app = useApp()
  return (
    <aside class="panel panel--right" aria-label="Inspector" data-testid="inspector">
      <Switch>
        <Match when={app.selectedObject()}>{(o) => <ObjectInspector id={o().id} />}</Match>
        <Match when={app.selectedSource()}>{(s) => <SourceInspector id={s().id} />}</Match>
        <Match when={app.ui.selection?.kind === 'head'}>
          <HeadInspector />
        </Match>
        <Match when={true}>
          <RoomInspector />
        </Match>
      </Switch>
    </aside>
  )
}

// ---- Room --------------------------------------------------------------------------------------------

function RoomInspector() {
  const { store, lastAcoustics } = useApp()
  const e = () => store.doc.enclosure
  const faces = () => ROOM_FACES[e().shape]

  /**
   * Change the room's shape or dimensions. Round and ring rooms are circular (x = z = diameter).
   * The head and sources are pulled back inside the new shell in the same undo step.
   */
  function applyRoom(change: { shape?: RoomShape; size?: Vec3; core?: number; outline?: Pt[] }) {
    const cur = store.plain().enclosure
    const shape = change.shape ?? cur.shape
    const min = ENCLOSURE_LIMITS.min
    const max = ENCLOSURE_LIMITS.max
    let size = (change.size ?? cur.size).map((v, i) => Math.min(max[i]!, Math.max(min[i]!, v))) as Vec3
    if (shape === 'round' || shape === 'ring') {
      // Entering a circular shape from a box: use the smaller footprint side as the diameter.
      const diameter = change.shape && cur.shape === 'box' && !change.size ? Math.min(size[0], size[2]) : change.size ? size[0] : size[0]
      const d = Math.min(max[0], Math.max(min[0], diameter))
      size = [d, size[1], d]
    }
    // Custom outline: keep it counter-clockwise and centred; width and depth then follow its bounds.
    let outline: Pt[] = []
    if (shape === 'polygon') {
      const given = change.outline ?? (cur.shape === 'polygon' && cur.outline.length >= 3 ? (cur.outline as Pt[]) : footprintOf(cur))
      outline = recenter(ccw(given)).points
      const b = bounds(outline)
      size = [b.maxX - b.minX, Math.min(max[1]!, Math.max(min[1]!, change.size?.[1] ?? cur.size[1])), b.maxZ - b.minZ]
    }
    let core = shape === 'ring' ? (change.core ?? (cur.core > 0 ? cur.core : size[0] * 0.3)) : 0
    core = shape === 'ring' ? Math.min(Math.max(core, 1), size[0] * 0.8) : 0
    const next = { ...cur, shape, size, core, outline }
    const plain = store.plain()
    const preview = { ...plain, enclosure: next }
    const items: Parameters<typeof store.patchMany>[0] = [{ sel: { c: 'enclosure' }, patch: { shape, size, core, outline } }]
    const lp = clampInside(preview, plain.listener.position, 0.3)
    if (lp.some((v, i) => v !== plain.listener.position[i])) items.push({ sel: { c: 'listener' }, patch: { position: lp } })
    for (const s of plain.sources) {
      const p = clampInside(preview, s.position, 0.3)
      if (p.some((v, i) => v !== s.position[i])) items.push({ sel: { c: 'sources', id: s.id }, patch: { position: p } })
    }
    store.patchMany(items, change.shape ? 'Change room shape' : 'Resize room')
  }

  /** The current floor plan as a polygon, used when switching to a custom outline. */
  function footprintOf(enc: Enclosure): Pt[] {
    const [w, , d] = enc.size
    if (enc.shape === 'box') {
      return [
        [-w / 2, -d / 2],
        [w / 2, -d / 2],
        [w / 2, d / 2],
        [-w / 2, d / 2],
      ]
    }
    return regular(32).map(([x, z]) => [x * w, z * d] as Pt)
  }

  function setAll(m: MaterialId) {
    const all = { floor: m, ceiling: m, west: m, east: m, north: m, south: m, wall: m, core: m } satisfies Enclosure['materials']
    store.patch({ c: 'enclosure' }, { materials: all }, 'Set all surfaces')
  }

  return (
    <>
      <Section title="Space">
        <Row label="Name">
          <input
            class="text"
            aria-label="Scene name"
            value={store.doc.name}
            onChange={(ev) => store.patch({ c: 'root' }, { name: ev.currentTarget.value.slice(0, 60) || 'Untitled' }, 'Rename scene')}
          />
        </Row>
        <div class="row row--stack">
          <span class="row__label">Shape</span>
          <Segmented
            label="Room shape"
            testid="room-shape"
            value={e().shape}
            onChange={(shape) => shape !== e().shape && applyRoom({ shape })}
            options={[
              { value: 'box', label: 'Box', title: 'Four straight walls' },
              { value: 'round', label: 'Round', title: 'A cylindrical hall' },
              { value: 'ring', label: 'Ring', title: 'A cylindrical hall around a solid core column' },
              { value: 'polygon', label: 'Custom', title: 'Draw your own floor plan' },
            ]}
          />
        </div>
        <Show when={e().shape === 'box'}>
          <Vec3Input
            label="Size (m)"
            axes={['W', 'H', 'D']}
            value={e().size}
            min={2}
            max={1500}
            step={1}
            testid="room-size"
            onCommit={(size) => applyRoom({ size })}
          />
        </Show>
        <Show when={e().shape === 'round' || e().shape === 'ring'}>
          <Row label="Diameter (m)">
            <NumberInput
              label="Room diameter"
              testid="room-diameter"
              value={e().size[0]}
              min={4}
              max={1500}
              step={1}
              onCommit={(v) => applyRoom({ size: [v, e().size[1], v] })}
            />
          </Row>
          <Row label="Height (m)">
            <NumberInput
              label="Room height"
              testid="room-height"
              value={e().size[1]}
              min={2}
              max={600}
              step={1}
              onCommit={(v) => applyRoom({ size: [e().size[0], v, e().size[2]] })}
            />
          </Row>
          <Show when={e().shape === 'ring'}>
            <Row label="Core diameter (m)">
              <NumberInput
                label="Core diameter"
                testid="room-core"
                value={e().core}
                min={1}
                max={e().size[0] * 0.8}
                step={1}
                onCommit={(v) => applyRoom({ core: v })}
              />
            </Row>
          </Show>
        </Show>
        <Show when={e().shape === 'polygon'}>
          <Row label="Height (m)">
            <NumberInput
              label="Room height"
              testid="room-height"
              value={e().size[1]}
              min={2}
              max={600}
              step={1}
              onCommit={(v) => applyRoom({ size: [e().size[0], v, e().size[2]] })}
            />
          </Row>
          <OutlineEditor
            testid="room-outline"
            contours={[e().outline as Pt[]]}
            snap={store.doc.settings.snapEnabled ? store.doc.settings.snapStep : 0}
            presetSize={[Math.max(e().size[0], 4), Math.max(e().size[2], 4)]}
            maxPoints={MAX_OUTLINE_POINTS}
            onCommit={([outline]) => {
              applyRoom({ outline })
            }}
          />
        </Show>{' '}
        <p class="note">
          {e().shape === 'box'
            ? 'Floor at y = 0, centred on the origin. Width runs east–west, depth north–south.'
            : e().shape === 'polygon'
              ? 'Floor at y = 0. The outline is kept centred and counter-clockwise. Walls between you and a source shadow it like a corner would.'
              : 'Floor at y = 0, centred on the origin. Sound can travel the long way round the curve.'}
        </p>
      </Section>
      <Section title="Surfaces">
        <SelectRow
          label="All"
          value={(MATERIAL_IDS.find((m) => faces().every((f) => e().materials[f] === m)) ?? 'concrete') as MaterialId}
          options={MATERIAL_OPTIONS}
          onChange={setAll}
        />
        <For each={faces()}>
          {(face) => (
            <SelectRow
              label={FACE_LABELS[face]}
              value={e().materials[face]}
              options={MATERIAL_OPTIONS}
              testid={`mat-${face}`}
              onChange={(m) =>
                store.patch(
                  { c: 'enclosure' },
                  { materials: { ...store.plain().enclosure.materials, [face]: m } },
                  `Set ${FACE_LABELS[face]}`,
                )
              }
            />
          )}
        </For>
      </Section>
      <Show when={lastAcoustics()}>
        {(a) => (
          <Section title="Room acoustics">
            <dl class="stats" data-testid="room-stats">
              <dt>Volume</dt>
              <dd>{Math.round(a().room.volume).toLocaleString()} m³</dd>
              <dt>Decay (low)</dt>
              <dd>{round(a().room.rt60Low, 1)} s</dd>
              <dt>Decay (high)</dt>
              <dd>{round(a().room.rt60High, 1)} s</dd>
              <dt>Critical distance</dt>
              <dd>{round(a().room.criticalDistance, 1)} m</dd>
              <dt>Mean free path</dt>
              <dd>{round(a().room.meanFreePath, 1)} m</dd>
            </dl>
          </Section>
        )}
      </Show>
    </>
  )
}
// ---- Primitive ---------------------------------------------------------------------------------------

function ObjectInspector(props: { id: string }) {
  const app = useApp()
  const { store } = app
  const o = () => store.doc.objects.find((x) => x.id === props.id) as Primitive
  const sel = () => ({ c: 'objects', id: props.id }) as const
  return (
    <Show when={o()}>
      <Section title={o().kind === 'box' ? 'Box' : o().kind === 'tube' ? 'Tube' : o().kind === 'prism' ? 'Custom shape' : 'Cylinder'}>
        <Row label="Name">
          <input
            class="text"
            aria-label="Object name"
            value={o().name}
            onChange={(e) => store.patch(sel(), { name: e.currentTarget.value.slice(0, 40) || 'Object' }, 'Rename')}
          />
        </Row>
        <SelectRow
          label="Material"
          value={o().material}
          options={MATERIAL_OPTIONS}
          onChange={(m) => store.patch(sel(), { material: m }, 'Change material')}
          testid="obj-material"
        />
        <Vec3Input
          label="Position (m)"
          value={o().position}
          step={app.store.doc.settings.snapStep}
          testid="obj-pos"
          onCommit={(v) => store.patch(sel(), { position: v }, 'Move object')}
        />
        <Vec3Input
          label="Rotation (°)"
          value={o().rotation.map(deg) as Vec3}
          step={15}
          places={1}
          testid="obj-rot"
          onCommit={(v) => store.patch(sel(), { rotation: v.map(rad) }, 'Rotate object')}
        />
        <Vec3Input
          label={
            o().kind === 'box' || o().kind === 'prism' ? 'Size (m)' : o().kind === 'tube' ? 'Ø x · length · Ø z' : 'Ø x · height · Ø z'
          }
          axes={o().kind === 'box' || o().kind === 'prism' ? ['W', 'H', 'D'] : ['Ø', 'H', 'Ø']}
          value={o().size}
          min={0.05}
          max={1500}
          step={app.store.doc.settings.snapStep}
          testid="obj-size"
          onCommit={(v) => store.patch(sel(), { size: v }, 'Resize object')}
        />
        <Show when={o().kind === 'prism'}>
          <OutlineEditor
            testid="shape-outline"
            allowHoles
            contours={[prismToMetres(o().outline as Pt[], o().size), ...(o().holes as Pt[][]).map((h) => prismToMetres(h, o().size))]}
            snap={store.doc.settings.snapEnabled ? store.doc.settings.snapStep : 0}
            presetSize={[Math.max(Math.abs(o().size[0]), 0.5), Math.max(Math.abs(o().size[2]), 0.5)]}
            maxPoints={MAX_OUTLINE_POINTS}
            maxHoles={MAX_HOLES}
            onCommit={([outline, ...holes]) =>
              store.patch(
                sel(),
                { outline: prismFromMetres(outline!, o().size), holes: holes.map((h) => prismFromMetres(h, o().size)) },
                'Edit shape outline',
              )
            }
          />
        </Show>
        <Show when={o().kind === 'tube'}>
          <Row label="Wall (m)" hint="Wall thickness. Sound passes through the open bore and is blocked by the wall.">
            <NumberInput
              label="Tube wall thickness"
              testid="tube-wall"
              value={o().wall}
              min={0.01}
              max={Math.max(0.02, 0.49 * Math.min(o().size[0], o().size[2]))}
              step={0.1}
              onCommit={(v) => store.patch(sel(), { wall: v }, 'Tube wall thickness')}
            />
          </Row>
          <SelectRow
            label="Ends"
            value={o().caps}
            options={CAP_OPTIONS}
            testid="tube-caps"
            onChange={(c) => store.patch(sel(), { caps: c }, 'Tube ends')}
          />
        </Show>
        <div class="actions">
          <Btn onClick={app.duplicateSelection} title="Duplicate (Ctrl+D)">
            Duplicate
          </Btn>
          <Btn danger onClick={app.deleteSelection} title="Delete (Del)" testid="delete-selection">
            Delete
          </Btn>
        </div>
      </Section>
    </Show>
  )
}

// ---- Source ------------------------------------------------------------------------------------------

function SourceInspector(props: { id: string }) {
  const app = useApp()
  const { store } = app
  const s = () => store.doc.sources.find((x) => x.id === props.id) as Source
  const sel = () => ({ c: 'sources', id: props.id }) as const
  const patch = (p: Record<string, unknown>, label: string, coalesce = false) => store.patch(sel(), p, label, coalesce)

  const clipMode = () => {
    const c = s().clip
    return !c ? 'loop' : c.retrigger.enabled ? 'retrigger' : c.loop ? 'loop' : 'once'
  }
  const patchClip = (p: Record<string, unknown>, label: string, coalesce = true) => {
    const c = store.plain().sources.find((x) => x.id === props.id)?.clip
    if (c) patch({ clip: { ...c, ...p } }, label, coalesce)
  }

  const setParam = (i: 0 | 1 | 2, v: number) => {
    const next = [...store.plain().sources.find((x) => x.id === props.id)!.params] as [number, number, number]
    next[i] = v
    patch({ params: next }, 'Adjust sound', true)
  }

  return (
    <Show when={s()}>
      <Section
        title={s().kind === 'clip' ? 'Clip source' : `${FAMILY_INFO[s().family].label} source`}
        aside={
          <span class="chip">
            {store.doc.sources.findIndex((x) => x.id === props.id) + 1}/{MAX_SOURCES}
          </span>
        }
      >
        <Row label="Name">
          <input
            class="text"
            aria-label="Source name"
            value={s().name}
            onChange={(e) => patch({ name: e.currentTarget.value.slice(0, 40) || 'Source' }, 'Rename')}
          />
        </Row>
        <div class="actions">
          <Btn
            accent={s().playing}
            active={s().playing}
            onClick={() => patch({ playing: !s().playing }, s().playing ? 'Stop source' : 'Play source')}
            testid="src-play"
          >
            {s().playing ? '■ Stop' : '▶ Play'}
          </Btn>
          <Btn active={s().mute} onClick={() => patch({ mute: !s().mute }, 'Mute')} testid="src-mute">
            M
          </Btn>
          <Btn active={s().solo} onClick={() => patch({ solo: !s().solo }, 'Solo')} testid="src-solo">
            S
          </Btn>
        </div>
        <SliderRow
          label="Level"
          hint="Level at the reference radius. Megastructure machines can be pushed up to +40 dB so they carry across the space."
          value={s().gain <= 0.01 ? -40 : 20 * Math.log10(s().gain)}
          min={-40}
          max={40}
          step={0.5}
          format={(v) => (v <= -40 ? '−∞ dB' : `${v > 0 ? '+' : ''}${round(v, 1)} dB`)}
          onInput={(v) => patch({ gain: v <= -40 ? 0 : 10 ** (v / 20) }, 'Source level', true)}
          testid="src-level"
        />
        <Row
          label="Size (m)"
          hint="Reference radius: level is constant inside it and falls as 1/distance beyond. A 40 m turbine is not a point."
        >
          <NumberInput
            label="Source size"
            value={s().size}
            min={0.1}
            max={500}
            step={1}
            testid="src-size"
            onCommit={(v) => patch({ size: v }, 'Source size')}
          />
        </Row>
        <SliderRow
          label="High-frequency cut"
          value={Math.log2(s().hfCut / 500) / Math.log2(40)}
          min={0}
          max={1}
          step={0.005}
          format={() => formatHz(s().hfCut)}
          onInput={(v) => patch({ hfCut: Math.round(500 * 40 ** v) }, 'High-frequency cut', true)}
        />
        <Vec3Input
          label="Position (m)"
          value={s().position}
          step={app.store.doc.settings.snapStep}
          testid="src-pos"
          onCommit={(v) => patch({ position: clampInside(store.plain(), v) }, 'Move source')}
        />
      </Section>

      <Show when={s().kind === 'generator'}>
        <Section title="Character">
          <For each={[0, 1, 2] as const}>
            {(i) => (
              <SliderRow
                label={FAMILY_INFO[s().family].controls[i]}
                value={s().params[i]}
                onInput={(v) => setParam(i, v)}
                format={(v) => `${Math.round(v * 100)}%`}
                testid={`src-param-${i}`}
              />
            )}
          </For>
          <Row label="Seed" hint="Same seed + same settings = the same irregularities every time.">
            <div class="seed">
              <NumberInput
                label="Seed"
                value={s().seed}
                step={1}
                min={0}
                max={4294967295}
                places={0}
                onCommit={(v) => patch({ seed: Math.floor(v) }, 'Change seed')}
              />
              <Btn
                small
                onClick={() => patch({ seed: Math.floor(Math.random() * 0xffffffff) }, 'Reroll seed')}
                title="Pick a new random seed"
              >
                ⟳
              </Btn>
            </div>
          </Row>
        </Section>
      </Show>

      <Show when={s().kind === 'clip' && s().clip}>
        <Section title="Clip playback">
          <p class="note">{s().clip!.fileName} — mono downmix</p>
          <Segmented
            label="Playback mode"
            value={clipMode()}
            onChange={(m) =>
              patchClip({ loop: m === 'loop', retrigger: { ...s().clip!.retrigger, enabled: m === 'retrigger' } }, 'Clip mode', false)
            }
            options={[
              { value: 'loop', label: 'Loop', title: 'Loop with a smoothed seam' },
              { value: 'retrigger', label: 'Retrigger', title: 'Irregular one-shot retriggering' },
              { value: 'once', label: 'Once' },
            ]}
          />
          <SliderRow
            label="Pitch"
            value={s().clip!.pitch}
            min={-24}
            max={24}
            step={0.5}
            format={(v) => `${v > 0 ? '+' : ''}${v} st`}
            onInput={(v) => patchClip({ pitch: v }, 'Clip pitch')}
          />
          <SliderRow
            label="Highpass"
            value={Math.log2(s().clip!.highpass / 20) / Math.log2(100)}
            format={() => `${Math.round(s().clip!.highpass)} Hz`}
            onInput={(v) => patchClip({ highpass: Math.round(20 * 100 ** v) }, 'Clip highpass')}
          />
          <SliderRow
            label="Lowpass"
            value={Math.log2(s().clip!.lowpass / 200) / Math.log2(100)}
            format={() => formatHz(s().clip!.lowpass)}
            onInput={(v) => patchClip({ lowpass: Math.round(200 * 100 ** v) }, 'Clip lowpass')}
          />
          <Show when={clipMode() === 'retrigger'}>
            <SliderRow
              label="Mean interval"
              value={s().clip!.retrigger.interval}
              min={0.5}
              max={30}
              step={0.1}
              format={(v) => `${round(v, 1)} s`}
              onInput={(v) => patchClip({ retrigger: { ...s().clip!.retrigger, interval: v } }, 'Retrigger interval')}
            />
            <SliderRow
              label="Irregularity"
              value={s().clip!.retrigger.irregularity}
              format={(v) => `${Math.round(v * 100)}%`}
              onInput={(v) => patchClip({ retrigger: { ...s().clip!.retrigger, irregularity: v } }, 'Retrigger irregularity')}
            />
          </Show>
        </Section>
      </Show>

      <div class="actions actions--pad">
        <Btn onClick={app.duplicateSelection} title="Duplicate (Ctrl+D)">
          Duplicate
        </Btn>
        <Btn danger onClick={app.deleteSelection} title="Delete (Del)" testid="delete-selection">
          Delete
        </Btn>
      </div>
    </Show>
  )
}

// ---- Head --------------------------------------------------------------------------------------------

function HeadInspector() {
  const app = useApp()
  const { store } = app
  const l = () => store.doc.listener
  return (
    <Section title="Listener head">
      <p class="note">One binaural listener. Blue ear is left, orange ear is right; the arrow points forward.</p>
      <Vec3Input
        label="Position (m)"
        value={l().position}
        step={0.5}
        testid="head-pos"
        onCommit={(v) => store.patch({ c: 'listener' }, { position: clampInside(store.plain(), v) }, 'Move listener')}
      />
      <Row label="Yaw (°)">
        <NumberInput
          label="Yaw"
          value={deg(l().yaw)}
          step={15}
          places={1}
          testid="head-yaw"
          onCommit={(v) => store.patch({ c: 'listener' }, { yaw: rad(v) }, 'Turn head')}
        />
      </Row>
      <Row label="Pitch (°)">
        <NumberInput
          label="Pitch"
          value={deg(l().pitch)}
          step={5}
          min={-85}
          max={85}
          places={1}
          testid="head-pitch"
          onCommit={(v) => store.patch({ c: 'listener' }, { pitch: rad(v) }, 'Tilt head')}
        />
      </Row>
      <div class="actions">
        <Btn onClick={app.listenHere} title="Move the head to the editor camera">
          Listen here
        </Btn>
      </div>
    </Section>
  )
}
